/**
 * RolesPageBase — shared list surface for Rollen & Rechte and Berechtigungsgruppen
 * (Story UI-U6 AC4). Same endpoints, split by `kind` ('rolle' | 'gruppe').
 *
 * List (per prototype): name + System-Tag, description, aktive Rechte n / total,
 * row action Ansehen (system) / Rechte bearbeiten. Create sends the respective
 * `kind`. The wizard handles create/edit/delete + the Sichtbarkeit deny section.
 */

import { useMemo, useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { AdminPageHeader } from '../AdminPageHeader';
import { useAdminRoles, useAdminRolesCatalog } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { Button, LoadingState, ErrorState, EmptyState } from '../components';
import { RoleWizardDialog, type WizardRole } from '../RoleWizardDialog';
import type { RoleDto } from '../types';

export function RolesPageBase({
  kind,
  title,
  description,
  createLabel,
  emptyLabel,
}: {
  kind: 'rolle' | 'gruppe';
  title: string;
  description: string;
  createLabel: string;
  emptyLabel: string;
}) {
  const { can } = useEffectivePermissions();
  const canManage = can('role.manage');
  const rolesQuery = useAdminRoles();
  const catalogQuery = useAdminRolesCatalog();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; role?: WizardRole } | null>(
    null,
  );

  const total = useMemo(
    () =>
      (catalogQuery.data?.categories ?? []).reduce((n, c) => n + c.permissions.length, 0),
    [catalogQuery.data],
  );

  const rows = useMemo(
    () => (rolesQuery.data?.roles ?? []).filter((r) => r.kind === kind),
    [rolesQuery.data, kind],
  );

  const openEdit = (r: RoleDto) =>
    setEditing({
      mode: 'edit',
      role: {
        id: r.id,
        name: r.name,
        description: r.description,
        is_system: r.is_system,
        is_wildcard: r.is_wildcard,
        ist_administrationsrolle: r.ist_administrationsrolle,
        selected_keys: r.permission_keys,
      },
    });

  return (
    <div>
      <AdminPageHeader
        title={title}
        description={description}
        actions={
          canManage && catalogQuery.data != null ? (
            <Button onClick={() => setEditing({ mode: 'create' })}>{createLabel}</Button>
          ) : undefined
        }
      />

      {rolesQuery.isLoading ? (
        <LoadingState />
      ) : rolesQuery.isError ? (
        <ErrorState />
      ) : rows.length === 0 ? (
        <EmptyState label={emptyLabel} />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const n = r.is_wildcard ? total : r.permission_keys.length;
            return (
              <li
                key={r.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{r.name}</span>
                    {r.is_system && <Badge variant="neutral">System</Badge>}
                  </div>
                  {r.description != null && (
                    <div className="truncate text-xs text-muted-foreground">{r.description}</div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {r.is_wildcard ? 'Alle Rechte' : `${n} / ${total} Rechte`}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => openEdit(r)}>
                    {r.is_system ? 'Ansehen' : 'Rechte bearbeiten'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing != null && catalogQuery.data != null && (
        <RoleWizardDialog
          // Remount per role, so no local wizard state can survive a role-to-role switch.
          key={editing.role?.id ?? 'create'}
          mode={editing.mode}
          kind={kind}
          role={editing.role}
          catalog={catalogQuery.data}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
